import { useEffect, useState, useMemo, useRef, useCallback, type ChangeEvent } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { ActionTypes } from "@/actions"
import { useAuth } from "../auth/AuthContext"
import { usePermission } from "../acl/usePermission"
import { RefreshCw, Plus, X, Database, ExternalLink, Download, Upload } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button as ShadcnButton } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import {
    Dialog as MuiDialog,
    DialogActions,
    DialogContent as MuiDialogContent,
    DialogContentText,
    DialogTitle as MuiDialogTitle,
} from "@mui/material"
import { Button } from "@mui/material"
import { PulseLoader } from "react-spinners"
import { toast } from "react-toastify"
import EditComponent, { type CollectionWizardChrome } from "../admin/EditComponent"
import { EMPTY_ARRAY } from "@/constants/reduxStableRefs"
import { buildCollectionPath } from "../utils/collectionPaths"
import fetchCollections from "../api/componentsData"
import { invalidateAllCachesUsedByFetchMeta } from "../api/tableData"
import { Checkbox } from "@/components/ui/checkbox"
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "@/components/ui/context-menu"
import { dataAPI } from "../api/dataApi"
import {
    buildCollectionExportBlob,
    downloadBlob,
    findMatchingComponent,
    findOtherComponentWithMongoCollection,
    findOtherComponentWithSlugOrganization,
    getMongoCollectionKey,
    getSlugOrganizationKey,
    mergeImportedForUpdate,
    parseCollectionConfigImportData,
    stripComponentForCreate,
} from "../admin/collectionConfigTransfer"

const USER_COLLECTION_SLUG = "users"

function getQuickSetFields(fields: any[]) {
    return fields?.filter(
        (f) =>
            f?.quickSetValue !== undefined &&
            f?.quickSetValue !== null &&
            String(f.quickSetValue).trim() !== ""
    ) || []
}

const CollectionsPage = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { user: authUser, refreshUser } = useAuth()
    const reduxUser = useSelector((state: any) => state.user)
    const user = authUser || reduxUser
    const userId = useMemo(
        () =>
            user?.uid ||
            user?.info?.uid ||
            user?.info?._id ||
            user?.info?.id ||
            user?.id ||
            user?._id ||
            null,
        [user]
    )
    const allComponents = useSelector((state: any) => state.components.data ?? EMPTY_ARRAY)
    const componentToEdit = useSelector((state: any) => state.component.current)
    const componentsLoading = useSelector((state: any) => state.components.loading) || false
    const isAdmin = Boolean(user?.admin ?? user?.info?.admin)
    const viewAdvancedRequest = useMemo(
        () => ({ action: "view_collection_advanced" as const, targetType: "collection" as const, targetId: "*" }),
        []
    )
    const { allowed: canViewCollectionAdvanced } = usePermission(viewAdvancedRequest, { enabled: Boolean(userId) })
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
    const [componentToDelete, setComponentToDelete] = useState<any>(null)
    const [deleteInProgress, setDeleteInProgress] = useState(false)
    const [showCreateEditDrawer, setShowCreateEditDrawer] = useState(false)
    const [newEditorNonce, setNewEditorNonce] = useState(0)
    const [collectionWizardUi, setCollectionWizardUi] = useState<CollectionWizardChrome | null>(null)
    const allowCloseWithoutConfirmRef = useRef(false)
    const editComponentRef = useRef<{
        save: () => Promise<boolean>
        hasInteracted: () => boolean
        wizardBack?: () => void
        requestCancel?: () => void
    }>(null)
    const isNewComponent = useSelector((state: any) => state.component.isNew)

    const [selectedUids, setSelectedUids] = useState<Set<string>>(() => new Set())
    const [importBusy, setImportBusy] = useState(false)
    const importFileRef = useRef<HTMLInputElement>(null)

    const ownComponents = useMemo(() => {
        if (!userId || !allComponents.length) return []
        return allComponents.filter(
            (c: any) => (c.createdBy === userId || c._createdBy === userId) && (c.slug?.toLowerCase() || "") !== USER_COLLECTION_SLUG
        )
    }, [allComponents, userId])

    const limit = user?.collectionLimit ?? user?.info?.collectionLimit
    const count = user?.collectionCount ?? user?.info?.collectionCount ?? 0
    const hasNoLimit = limit == null
    const atLimit = typeof limit === "number" && limit > 0 && count >= limit
    const canCreate = !hasNoLimit && !atLimit
    const showPendingLimitsMessage = !isAdmin && hasNoLimit

    useEffect(() => {
        if (!showPendingLimitsMessage) return
        const intervalMs = 30 * 60 * 1000
        const id = setInterval(() => window.location.reload(), intervalMs)
        return () => clearInterval(id)
    }, [showPendingLimitsMessage])

    useEffect(() => {
        if (userId && user) {
            dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user } })
        }
    }, [userId, dispatch, user])

    useEffect(() => {
        if (isRefreshing && !componentsLoading) {
            setIsRefreshing(false)
        }
    }, [componentsLoading, isRefreshing])

    useEffect(() => {
        if (location.state?.create === true && userId && canCreate) {
            setNewEditorNonce((n) => n + 1)
            dispatch({ type: ActionTypes.EDIT_COMPONENT_NEW, payload: { userId } })
            setShowCreateEditDrawer(true)
            navigate(location.pathname, { replace: true, state: {} })
        }
    }, [location.state?.create, location.pathname, userId, canCreate, dispatch, navigate])

    useEffect(() => {
        const editUid = (location.state as any)?.editUid
        if (!editUid || !allComponents.length) return
        const component = allComponents.find((c: any) => c.uid === editUid)
        if (component) {
            dispatch({ type: ActionTypes.EDIT_COMPONENT_START, payload: component })
            setShowCreateEditDrawer(true)
            navigate(location.pathname, { replace: true, state: {} })
        }
    }, [location.state, allComponents, dispatch, navigate, location.pathname])

    const refreshList = async (force: boolean = false): Promise<void> => {
        if (!user) return
        setIsRefreshing(true)
        dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user, force } })
        await refreshUser?.()
    }

    const handleOpenCollection = (component: { slug?: string; organization?: string | null }) => {
        const s = typeof component.slug === "string" ? component.slug.replace(/[!]+$/, "").trim() : ""
        if (!s) return
        navigate(buildCollectionPath({ slug: s, organization: component.organization }))
    }

    const handleDeleteClick = (component: any) => {
        const slug = (component?.slug || "").toLowerCase()
        if (slug === "users") {
            toast.error("The Users collection cannot be deleted.")
            return
        }
        setComponentToDelete(component)
        setOpenDeleteDialog(true)
    }

    const handleCloseDeleteDialog = () => {
        if (!deleteInProgress) {
            setOpenDeleteDialog(false)
            setComponentToDelete(null)
        }
    }

    const handleConfirmDelete = async () => {
        if (!componentToDelete?.uid) return
        setDeleteInProgress(true)
        dispatch({ type: ActionTypes.COMPONENT_DELETE, payload: componentToDelete.uid })
        setOpenDeleteDialog(false)
        setComponentToDelete(null)
        setDeleteInProgress(false)
    }

    const handleCreateCollection = () => {
        if (!canCreate) {
            const message = hasNoLimit
                ? "You cannot create collections yet. Please wait for an administrator to give you access."
                : "You cannot create more collections. Please wait for an administrator to increase your limit or grant access."
            toast.warn(message, { autoClose: 8000 })
            return
        }
        if (userId) {
            setNewEditorNonce((n) => n + 1)
            dispatch({ type: ActionTypes.EDIT_COMPONENT_NEW, payload: { userId } })
            setShowCreateEditDrawer(true)
        }
    }

    const handleEditClick = (component: any) => {
        dispatch({ type: ActionTypes.EDIT_COMPONENT_START, payload: component })
        setShowCreateEditDrawer(true)
    }

    const handleCloseCreateEditDrawer = () => {
        allowCloseWithoutConfirmRef.current = false
        setCollectionWizardUi(null)
        setShowCreateEditDrawer(false)
        refreshList()
    }

    const handleSavedOrCreated = async (createdComponent?: { slug?: string; uid?: string; organization?: string | null;[key: string]: any }) => {
        allowCloseWithoutConfirmRef.current = true
        setCollectionWizardUi(null)
        setShowCreateEditDrawer(false)
        invalidateAllCachesUsedByFetchMeta()
        if (createdComponent?.slug) {
            try {
                const list = await fetchCollections()
                dispatch({ type: ActionTypes.COMPONENTS_SUCCESS, payload: list })
            } catch {
                dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user, force: true } })
            }
        } else {
            dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user, force: true } })
        }
        await refreshUser?.()
        if (createdComponent?.slug) {
            const s = String(createdComponent.slug).replace(/[!]+$/, "").trim()
            if (s) {
                navigate(buildCollectionPath({ slug: s, organization: createdComponent.organization }))
            }
        }
    }

    const handleEditComponentCallback = () => {
        handleCloseCreateEditDrawer()
    }

    const handleEditInAdmin = (component: any) => {
        if (isAdmin) {
            navigate("/admin/collections", { state: { editUid: component.uid } })
        } else {
            handleEditClick(component)
        }
    }

    const selectedOwnComponents = useMemo(
        () => ownComponents.filter((c: any) => selectedUids.has(c.uid)),
        [ownComponents, selectedUids]
    )

    const allOwnSelected = ownComponents.length > 0 && selectedUids.size === ownComponents.length
    const someRowsSelected = selectedUids.size > 0

    const toggleSelectUid = (uid: string, checked: boolean) => {
        setSelectedUids((prev) => {
            const next = new Set(prev)
            if (checked) next.add(uid)
            else next.delete(uid)
            return next
        })
    }

    const toggleSelectAllOwn = () => {
        if (allOwnSelected) {
            setSelectedUids(new Set())
        } else {
            setSelectedUids(new Set(ownComponents.map((c: any) => c.uid)))
        }
    }

    const clearSelection = () => setSelectedUids(new Set())

    const exportConfigsToFile = (list: any[], filename: string) => {
        if (!list.length) {
            toast.warn("No collections selected.")
            return
        }
        downloadBlob(buildCollectionExportBlob(list), filename)
    }

    const handleExportSelected = () => {
        exportConfigsToFile(selectedOwnComponents, `collections-export-${selectedOwnComponents.length}.json`)
    }

    const handleExportAllOwn = () => {
        exportConfigsToFile(ownComponents, "my-collections-export-all.json")
    }

    const handleExportOne = (component: any) => {
        const slug = String(component?.slug || "config").replace(/[^a-z0-9_-]/gi, "_")
        exportConfigsToFile([component], `collection-${slug}.json`)
    }

    const handleImportFile = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            e.target.value = ""
            if (!file || !user) return
            setImportBusy(true)
            try {
                const text = await file.text()
                let parsed: unknown
                try {
                    parsed = JSON.parse(text)
                } catch {
                    toast.error("Invalid JSON file.")
                    return
                }
                const items = parseCollectionConfigImportData(parsed)
                if (!items.length) {
                    toast.error("No collection configurations found in file.")
                    return
                }
                let created = 0
                let updated = 0
                let skipped = 0
                const errors: string[] = []
                const mongoKeyOwnerInBatch = new Map<string, string>()
                const slugOrgOwnerInBatch = new Map<string, string>()
                for (const raw of items) {
                    const comp = raw as Record<string, unknown>
                    const label = String(comp.name || comp.slug || "entry")
                    const slug = String(comp.slug || "")
                        .trim()
                        .toLowerCase()
                        .replace(/[!]+$/, "")
                    if (slug === "users") {
                        skipped++
                        continue
                    }
                    if (!comp.slug || !Array.isArray(comp.fields)) {
                        errors.push(`${label}: missing slug or fields`)
                        continue
                    }
                    const matchOwn = findMatchingComponent(ownComponents as any[], comp)
                    const matchAny = findMatchingComponent(allComponents as any[], comp)
                    if (!matchOwn && matchAny) {
                        skipped++
                        errors.push(`${label}: another user owns this slug — skipped`)
                        continue
                    }
                    const match = matchOwn
                    const excludeUid = match ? String((match as any).uid) : null
                    const mongoKey = getMongoCollectionKey(comp)
                    if (mongoKey) {
                        if (mongoKeyOwnerInBatch.has(mongoKey)) {
                            const priorUid = mongoKeyOwnerInBatch.get(mongoKey)!
                            if (excludeUid !== priorUid) {
                                errors.push(
                                    `${label}: mongoCollection "${mongoKey}" is already used in this import batch`
                                )
                                continue
                            }
                        }
                        const conflict = findOtherComponentWithMongoCollection(
                            allComponents as any[],
                            mongoKey,
                            excludeUid
                        )
                        if (conflict) {
                            const other = conflict as Record<string, unknown>
                            const otherLabel = String(other.name || other.slug || "another collection")
                            errors.push(
                                `${label}: mongoCollection "${mongoKey}" is already used by "${otherLabel}"`
                            )
                            continue
                        }
                    }
                    const slugOrgKey = getSlugOrganizationKey(comp)
                    if (slugOrgOwnerInBatch.has(slugOrgKey)) {
                        const priorUid = slugOrgOwnerInBatch.get(slugOrgKey)!
                        if (excludeUid !== priorUid) {
                            errors.push(
                                `${label}: slug and organization already used in this import batch`
                            )
                            continue
                        }
                    }
                    const slugConflict = findOtherComponentWithSlugOrganization(
                        allComponents as any[],
                        comp,
                        excludeUid
                    )
                    if (slugConflict) {
                        const other = slugConflict as Record<string, unknown>
                        const otherLabel = String(other.name || other.slug || "another collection")
                        errors.push(
                            `${label}: slug/organization already used by "${otherLabel}"`
                        )
                        continue
                    }
                    try {
                        let res: unknown
                        if (match) {
                            const merged = mergeImportedForUpdate(comp, matchOwn as Record<string, unknown>)
                            res = await dataAPI.updateComponent(String((matchOwn as any).uid), merged)
                            updated++
                        } else {
                            res = await dataAPI.createComponent(stripComponentForCreate(comp))
                            created++
                        }
                        const r = res as { component?: { uid?: string }; data?: { uid?: string } }
                        const uidForBatch = match
                            ? String((matchOwn as any).uid)
                            : String(r?.component?.uid || r?.data?.uid || "")
                        if (uidForBatch) {
                            if (mongoKey) mongoKeyOwnerInBatch.set(mongoKey, uidForBatch)
                            slugOrgOwnerInBatch.set(slugOrgKey, uidForBatch)
                        }
                    } catch (err) {
                        errors.push(`${label}: ${err instanceof Error ? err.message : String(err)}`)
                    }
                }
                await refreshList(true)
                await refreshUser?.()
                clearSelection()
                toast.success(
                    `Import finished: ${created} created, ${updated} updated, ${skipped} skipped${errors.length ? `, ${errors.length} issues` : ""}.`
                )
                if (errors.length) {
                    toast.error(errors.slice(0, 5).join("; "), { autoClose: 12000 })
                }
            } finally {
                setImportBusy(false)
            }
        },
        [allComponents, ownComponents, refreshList, refreshUser, user]
    )

    return (
        <>
            <div className="h-full flex flex-col min-h-0">
                {showPendingLimitsMessage ? (
                    <div className="flex-1 flex items-start justify-center p-6 sm:p-8">
                        <Alert className="max-w-2xl">
                            <AlertDescription>
                                Your account is pending setup. An administrator needs to set your collection limits before you can create collections. Please wait for an administrator to increase your limits.
                            </AlertDescription>
                        </Alert>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between flex-none p-6 sm:p-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                            <div>
                                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-3">
                                    Collection manager
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400">
                                    Collections you created. Open to view data, or manage in Admin.
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                <ShadcnButton
                                    variant="outline"
                                    onClick={() => refreshList(true)}
                                    disabled={!user || isRefreshing}
                                    className="flex items-center gap-2"
                                    aria-label="Reload collections"
                                >
                                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
                                    <span>{isRefreshing ? "Refreshing..." : "Reload"}</span>
                                </ShadcnButton>
                                <input
                                    ref={importFileRef}
                                    type="file"
                                    accept=".json,application/json"
                                    className="hidden"
                                    onChange={handleImportFile}
                                />
                                <ShadcnButton
                                    type="button"
                                    variant="outline"
                                    disabled={!user || importBusy}
                                    onClick={() => importFileRef.current?.click()}
                                    className="flex items-center gap-2"
                                    aria-label="Import collection configs"
                                >
                                    <Upload className="w-4 h-4" />
                                    <span>{importBusy ? "Importing…" : "Import"}</span>
                                </ShadcnButton>
                                <ShadcnButton
                                    type="button"
                                    variant="outline"
                                    disabled={!user || selectedOwnComponents.length === 0}
                                    onClick={handleExportSelected}
                                    className="flex items-center gap-2"
                                    aria-label="Export selected collection configs"
                                >
                                    <Download className="w-4 h-4" />
                                    <span>Export selected</span>
                                </ShadcnButton>
                                <ShadcnButton
                                    type="button"
                                    variant="outline"
                                    disabled={!user || ownComponents.length === 0}
                                    onClick={handleExportAllOwn}
                                    className="flex items-center gap-2"
                                    aria-label="Export all my collection configs"
                                >
                                    <Download className="w-4 h-4" />
                                    <span>Export all</span>
                                </ShadcnButton>
                                <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <ShadcnButton
                                                onClick={handleCreateCollection}
                                                disabled={!canCreate}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Plus className="w-5 h-5" />
                                                <span>New Collection</span>
                                            </ShadcnButton>
                                        </TooltipTrigger>
                                        {!canCreate && (
                                            <TooltipContent className="text-base px-4 py-2">
                                                <p>Limit reached</p>
                                            </TooltipContent>
                                        )}
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>

                        {someRowsSelected ? (
                            <div className="flex-none flex flex-wrap items-center gap-2 px-6 sm:px-8 py-2 border-b border-gray-200 dark:border-gray-700 bg-muted/40 text-sm text-gray-700 dark:text-gray-300">
                                <span className="font-medium">{selectedUids.size} selected</span>
                                <ShadcnButton type="button" variant="ghost" size="sm" onClick={clearSelection}>
                                    Clear
                                </ShadcnButton>
                                <ShadcnButton type="button" variant="secondary" size="sm" onClick={handleExportSelected}>
                                    <Download className="w-4 h-4 mr-1" />
                                    Export selected
                                </ShadcnButton>
                            </div>
                        ) : null}

                        {componentsLoading && ownComponents.length === 0 ? (
                            <div className="flex-1 flex items-center justify-center p-8">
                                <div className="text-center">
                                    <RefreshCw className="w-10 h-10 mx-auto text-gray-400 animate-spin mb-4" />
                                    <p className="text-gray-500 dark:text-gray-400">Loading your collections...</p>
                                </div>
                            </div>
                        ) : ownComponents.length > 0 ? (
                            <div className="flex-1 min-h-0 overflow-y-auto w-full p-6">
                                <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm overflow-x-auto pb-12">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="hover:bg-transparent border-gray-200 dark:border-gray-800">
                                                <TableHead className="w-10 pl-3 align-middle">
                                                    <Checkbox
                                                        checked={allOwnSelected}
                                                        onCheckedChange={() => toggleSelectAllOwn()}
                                                        aria-label="Select all my collections"
                                                    />
                                                </TableHead>
                                                <TableHead className="text-gray-900 dark:text-gray-100 min-w-[10rem]">Name</TableHead>
                                                <TableHead className="text-gray-900 dark:text-gray-100">Slug</TableHead>
                                                <TableHead className="text-gray-900 dark:text-gray-100 min-w-[7rem]">UID</TableHead>
                                                <TableHead className="text-gray-900 dark:text-gray-100 text-right w-24">Fields</TableHead>
                                                <TableHead className="text-gray-900 dark:text-gray-100 min-w-[8rem] max-w-[14rem]">Quick set</TableHead>
                                                <TableHead className="text-gray-900 dark:text-gray-100 w-28">Features</TableHead>
                                                <TableHead className="text-gray-900 dark:text-gray-100 whitespace-nowrap w-32">Updated</TableHead>
                                                <TableHead className="text-gray-900 dark:text-gray-100 text-right w-[1%]">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {ownComponents.map((component: any) => {
                                                const fields = component.fields || []
                                                const fieldCount = fields.length
                                                const quickSetFields = getQuickSetFields(fields)
                                                const previewLabels = fields
                                                    .slice(0, 3)
                                                    .map((f: any) => f.label || f.name)
                                                    .filter(Boolean)
                                                    .join(", ")
                                                const extraFields = fieldCount > 3 ? fieldCount - 3 : 0
                                                const showGroupsBadge =
                                                    (isAdmin || canViewCollectionAdvanced) && Boolean(component.showGroups)
                                                const updatedRaw =
                                                    component.dataSource === "mongodb"
                                                        ? component.updated || component.created
                                                        : null
                                                const updatedLabel = updatedRaw
                                                    ? new Date(updatedRaw).toLocaleDateString()
                                                    : "—"
                                                return (
                                                    <ContextMenu key={component.uid}>
                                                        <ContextMenuTrigger asChild>
                                                            <TableRow
                                                                className="border-gray-200 dark:border-gray-800 align-top cursor-default"
                                                                onDoubleClick={() => handleEditInAdmin(component)}
                                                            >
                                                                <TableCell
                                                                    className="py-4 pl-3 align-top w-10"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onDoubleClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <Checkbox
                                                                        checked={selectedUids.has(component.uid)}
                                                                        onCheckedChange={(c) =>
                                                                            toggleSelectUid(component.uid, c === true)
                                                                        }
                                                                        aria-label={`Select ${component.name || component.slug}`}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </TableCell>
                                                                <TableCell className="font-medium text-gray-900 dark:text-gray-100 py-4">
                                                                    {component.name}
                                                                </TableCell>
                                                                <TableCell className="py-4 align-top">
                                                                    <Badge
                                                                        variant="secondary"
                                                                        className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 text-xs font-normal"
                                                                    >
                                                                        {component.slug}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell
                                                                    className="font-mono text-xs text-gray-600 dark:text-gray-400 max-w-[12rem] truncate py-4 align-top"
                                                                    title={component.uid}
                                                                >
                                                                    {component.uid}
                                                                </TableCell>
                                                                <TableCell className="text-right align-top py-4">
                                                                    <div className="tabular-nums text-gray-900 dark:text-gray-100">{fieldCount}</div>
                                                                    {previewLabels ? (
                                                                        <div
                                                                            className="text-xs text-muted-foreground mt-1 max-w-[14rem] text-right ml-auto line-clamp-2"
                                                                            title={
                                                                                extraFields > 0
                                                                                    ? `${previewLabels} (+${extraFields} more)`
                                                                                    : previewLabels
                                                                            }
                                                                        >
                                                                            {previewLabels}
                                                                            {extraFields > 0 ? ` (+${extraFields})` : ""}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-xs text-muted-foreground mt-1">—</div>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="text-sm text-gray-600 dark:text-gray-400 max-w-[14rem] py-4 align-top">
                                                                    {quickSetFields.length > 0 ? (
                                                                        <span className="line-clamp-2" title={quickSetFields.map((f: any) => f.label || f.name).join(", ")}>
                                                                            {quickSetFields.map((f: any) => f.label || f.name).join(", ")}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-muted-foreground">—</span>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="py-4 align-top">
                                                                    {showGroupsBadge ? (
                                                                        <Badge
                                                                            variant="secondary"
                                                                            className="bg-green-100 text-green-800 dark:bg-green-900/50 text-xs"
                                                                        >
                                                                            Groups
                                                                        </Badge>
                                                                    ) : (
                                                                        <span className="text-muted-foreground text-sm">—</span>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap py-4 align-top">
                                                                    {updatedLabel}
                                                                </TableCell>
                                                                <TableCell
                                                                    className="text-right py-4 align-top"
                                                                    onDoubleClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div className="flex items-center justify-end gap-1 flex-nowrap">
                                                                        <ShadcnButton
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            aria-label="Open collection"
                                                                            onClick={() => handleOpenCollection(component)}
                                                                            className="text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 h-8 w-8"
                                                                        >
                                                                            <ExternalLink className="w-4 h-4" />
                                                                        </ShadcnButton>
                                                                        <ShadcnButton
                                                                            variant="outline"
                                                                            size="sm"
                                                                            aria-label={isAdmin ? "Edit in Admin" : "Edit"}
                                                                            onClick={() => handleEditInAdmin(component)}
                                                                            className="h-8 px-2 text-gray-600 dark:text-gray-300"
                                                                        >
                                                                            Edit
                                                                        </ShadcnButton>
                                                                        <ShadcnButton
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            aria-label="Delete collection"
                                                                            onClick={() => handleDeleteClick(component)}
                                                                            className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 h-8 w-8"
                                                                        >
                                                                            <X className="w-4 h-4" />
                                                                        </ShadcnButton>
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                        </ContextMenuTrigger>
                                                        <ContextMenuContent className="min-w-[12rem]">
                                                            <ContextMenuItem
                                                                onSelect={() => handleOpenCollection(component)}
                                                            >
                                                                Open collection
                                                            </ContextMenuItem>
                                                            <ContextMenuItem
                                                                onSelect={() => handleEditInAdmin(component)}
                                                            >
                                                                {isAdmin ? "Edit in Admin" : "Edit configuration"}
                                                            </ContextMenuItem>
                                                            <ContextMenuItem
                                                                onSelect={() => handleExportOne(component)}
                                                            >
                                                                Export config
                                                            </ContextMenuItem>
                                                            {someRowsSelected ? (
                                                                <ContextMenuItem onSelect={() => handleExportSelected()}>
                                                                    Export selected ({selectedUids.size})
                                                                </ContextMenuItem>
                                                            ) : null}
                                                            <ContextMenuSeparator />
                                                            <ContextMenuItem onSelect={() => handleExportAllOwn()}>
                                                                Export all my collections
                                                            </ContextMenuItem>
                                                            <ContextMenuItem
                                                                onSelect={() => importFileRef.current?.click()}
                                                            >
                                                                Import configs…
                                                            </ContextMenuItem>
                                                            <ContextMenuSeparator />
                                                            <ContextMenuItem
                                                                className="text-destructive focus:text-destructive"
                                                                onSelect={() => handleDeleteClick(component)}
                                                            >
                                                                Delete collection
                                                            </ContextMenuItem>
                                                        </ContextMenuContent>
                                                    </ContextMenu>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 min-h-0 flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-900/50 dark:to-gray-900">
                                <div className="max-w-md w-full px-6 py-12 text-center">
                                    <div className="w-20 h-20 mx-auto mb-6 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center ring-4 ring-blue-100 dark:ring-blue-900/30">
                                        <Database className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No collections yet</h3>
                                    <p className="text-gray-600 dark:text-gray-400 mb-8">
                                        Create a collection to define a data structure and start adding records.
                                    </p>
                                    <TooltipProvider delayDuration={0}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <ShadcnButton
                                                    onClick={handleCreateCollection}
                                                    disabled={!canCreate}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 mx-auto text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <Plus className="w-5 h-5" />
                                                    <span>Create Collection</span>
                                                </ShadcnButton>
                                            </TooltipTrigger>
                                            {!canCreate && (
                                                <TooltipContent className="text-base px-4 py-2">
                                                    <p>Limit reached</p>
                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {showCreateEditDrawer && (
                <Dialog
                    open={showCreateEditDrawer}
                    onOpenChange={(open) => {
                        if (!open) {
                            if (allowCloseWithoutConfirmRef.current) {
                                handleCloseCreateEditDrawer()
                                return
                            }
                            if (editComponentRef.current?.hasInteracted?.()) {
                                if (window.confirm("Discard unsaved changes?")) {
                                    handleCloseCreateEditDrawer()
                                }
                            } else {
                                handleCloseCreateEditDrawer()
                            }
                        }
                    }}
                >
                    <DialogContent className="!fixed !left-[1vw] !right-[1vw] !top-[6vh] !bottom-[6vh] !m-0 !h-auto !max-h-none !w-auto !max-w-none !p-0 !gap-0 !flex !flex-col !overflow-hidden !translate-x-0 !translate-y-0 [&>button]:hidden">
                        <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
                            <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900 m-0 truncate">
                                {isNewComponent
                                    ? collectionWizardUi
                                        ? `Create collection · Step ${collectionWizardUi.step} of ${collectionWizardUi.totalSteps}`
                                        : "Create collection"
                                    : componentToEdit?.name
                                        ? `Edit ${componentToEdit.name}`
                                        : "Edit collection"}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain edit-dialog-scrollable p-5 sm:p-7 bg-gray-200 dark:bg-gray-950">
                            <EditComponent
                                key={isNewComponent ? `new-${newEditorNonce}` : componentToEdit?.uid || "edit"}
                                ref={editComponentRef}
                                onEditComponent={handleEditComponentCallback}
                                onNewComponentCreated={handleSavedOrCreated}
                                hideActionButtons={true}
                                onCollectionWizardUiChange={setCollectionWizardUi}
                            />
                        </div>
                        <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                            {isNewComponent && collectionWizardUi?.showBack ? (
                                <ShadcnButton
                                    type="button"
                                    variant="outline"
                                    onClick={() => editComponentRef.current?.wizardBack?.()}
                                    className="px-4 py-2"
                                >
                                    Back
                                </ShadcnButton>
                            ) : null}
                            <ShadcnButton
                                variant="outline"
                                onClick={() => editComponentRef.current?.requestCancel?.()}
                                className="px-4 py-2"
                            >
                                Cancel
                            </ShadcnButton>
                            <ShadcnButton
                                disabled={Boolean(isNewComponent && collectionWizardUi?.nextDisabled)}
                                title={
                                    isNewComponent && collectionWizardUi?.nextDisabledReason
                                        ? collectionWizardUi.nextDisabledReason
                                        : undefined
                                }
                                onClick={async () => {
                                    if (editComponentRef.current?.save) {
                                        const ok = await editComponentRef.current.save();
                                        if (ok) handleSavedOrCreated();
                                    }
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 disabled:opacity-50 disabled:pointer-events-none"
                            >
                                {isNewComponent
                                    ? collectionWizardUi?.primaryLabel ?? "Next"
                                    : componentToEdit?.name
                                        ? "Save changes"
                                        : "Save"}
                            </ShadcnButton>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            <MuiDialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} aria-labelledby="delete-collection-dialog">
                <MuiDialogTitle>{componentToDelete?.name || "Collection"}</MuiDialogTitle>
                <MuiDialogContent>
                    <DialogContentText>Are you sure you want to delete this collection?</DialogContentText>
                </MuiDialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} disabled={deleteInProgress}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" disabled={deleteInProgress}>
                        {deleteInProgress ? <PulseLoader size={8} /> : "Delete"}
                    </Button>
                </DialogActions>
            </MuiDialog>
        </>
    )
}

export default CollectionsPage
